import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type FC,
  type PropsWithChildren,
} from "react";
import { useLocation } from "react-router-dom";
import { getDocsSlugForPath } from "src/config/docs-mapping";

interface DocsDrawerContextValue {
  isOpen: boolean;
  docsSlug: string | null;
  openDocs: (slug?: string) => void;
  closeDocs: () => void;
  toggleDocs: () => void;
  /** true when inside the DocsDrawerProvider */
  mounted: boolean;
}

const DocsDrawerContext = createContext<DocsDrawerContextValue>({
  isOpen: false,
  docsSlug: null,
  openDocs: () => {},
  closeDocs: () => {},
  toggleDocs: () => {},
  mounted: false,
});

export const useDocsDrawer = () => useContext(DocsDrawerContext);

export const DocsDrawerProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [explicitSlug, setExplicitSlug] = useState<string | null>(null);
  const location = useLocation();

  // Auto-resolve docs slug from current route
  const autoSlug = getDocsSlugForPath(location.pathname);
  const docsSlug = explicitSlug || autoSlug;

  const openDocs = useCallback((slug?: string) => {
    if (slug) {
      setExplicitSlug(slug);
    } else {
      setExplicitSlug(null);
    }
    setIsOpen(true);
  }, []);

  const closeDocs = useCallback(() => {
    setIsOpen(false);
    setExplicitSlug(null);
  }, []);

  const toggleDocs = useCallback(() => {
    setIsOpen(prev => {
      if (prev) setExplicitSlug(null);
      return !prev;
    });
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setIsOpen(false);
    setExplicitSlug(null);
  }, [location.pathname]);

  return (
    <DocsDrawerContext.Provider
      value={{
        isOpen,
        docsSlug,
        openDocs,
        closeDocs,
        toggleDocs,
        mounted: true,
      }}
    >
      {children}
    </DocsDrawerContext.Provider>
  );
};
